import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../services/api';
import { useCity } from '../contexts/CityContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from './LinesPage.module.css';
import LineChip from '../components/common/LineChip';
import AdBanner from '../components/ads/AdBanner';

interface BusLine {
  bus_line_id: string;
  bus_line_name: string;
  bus_line_path_description: string;
  description: string;
  start_bus_stop: string;
  end_bus_stop: string;
  bus_line_color: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  operator: {
    operator_id: number;
    operator_name: string;
    operator_logo?: string;
  };
}

interface ApiResponse {
  data: BusLine[];
  status: string;
  timestamp: string;
}

type TransportMode = 'bus' | 'metro' | 'tram' | 'train' | 'ferry';

function LinesPage() {
  const { currentCity, loading: cityLoading } = useCity();
  const { isPremium } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [allLines, setAllLines] = useState<BusLine[]>([]);
  const [filteredLines, setFilteredLines] = useState<BusLine[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedMode, setSelectedMode] = useState<TransportMode>('bus');
  const api = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentCity) {
      console.log('Aktif şehir API URL:', currentCity.apiEndpoint);
    } else {
      console.log('Aktif şehir seçili değil');
    }
  }, [currentCity]);

  const getActiveTransportModes = useCallback(() => {
    if (!currentCity) {
      console.log('Şehir seçili değil');
      return [];
    }

    try {
      const features = currentCity.features || {};
      console.log('Ulaşım modu özellikleri:', features);
      
      const modes: { id: TransportMode; name: string; active: boolean }[] = [
        { 
          id: 'bus', 
          name: 'Otobüs', 
          active: !!features.bus
        },
        { 
          id: 'metro', 
          name: 'Metro', 
          active: !!features.metro
        },
        { 
          id: 'tram', 
          name: 'Tramvay', 
          active: !!features.tram
        },
        { 
          id: 'train', 
          name: 'Tren', 
          active: !!features.train
        },
        { 
          id: 'ferry', 
          name: 'Vapur', 
          active: !!features.ferry
        },
      ];

      modes.forEach(mode => {
        console.log(`${mode.name} modu: ${mode.active ? 'Aktif' : 'Pasif'}`);
      });

      const activeModesFiltered = modes.filter(mode => mode.active);
      console.log('Aktif ulaşım modları:', activeModesFiltered);
      return activeModesFiltered;
    } catch (err) {
      console.error('Ulaşım modları kontrol edilirken hata oluştu:', err);
      return [];
    }
  }, [currentCity]);

  const activeTransportModes = getActiveTransportModes();
  const hasNoActiveTransportModes = activeTransportModes.length === 0;

  const applyFilters = useCallback((lines: BusLine[], mode: TransportMode, search: string = '') => {
    if (!lines || lines.length === 0) return;
    
    let filtered = [...lines];
    
    if (search) {
      filtered = filtered.filter(line => 
        line.bus_line_id?.toLowerCase().includes(search.toLowerCase()) || 
        line.bus_line_name?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    const prefixMap: Record<string, string[]> = {
      metro: ['M'],
      tram: ['T'],
      train: ['B', 'YHT'],
      ferry: ['F', 'V', 'D']
    };
    
    const prefixes = prefixMap[mode as keyof typeof prefixMap];
    if (mode !== 'bus' && prefixes) {
      filtered = filtered.filter(line => 
        prefixes.some(prefix => line.bus_line_id.toUpperCase().startsWith(prefix))
      );
    }
    
    console.log(`Filtreleme sonrası ${filtered.length} hat kaldı (Mod: ${mode}, Arama: ${search})`);
    setFilteredLines(filtered);
  }, []);

  useEffect(() => {
    if (activeTransportModes.length > 0) {
      const firstMode = activeTransportModes[0].id;
      console.log('İlk ulaşım modu seçildi:', firstMode);
      setSelectedMode(firstMode);
    }
  }, [activeTransportModes]);

  useEffect(() => {
    let isMounted = true;
    let controller: AbortController | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    
    const fetchLines = async () => {
      if (cityLoading) {
        setLoading(true);
        console.log('Şehir yükleniyor, API çağrısı bekletiliyor...');
        return;
      }
      
      if (!currentCity) {
        console.log('Şehir seçili olmadığı için hatlar yüklenemiyor');
        setError('Lütfen önce bir şehir seçin. Sağ üstteki şehir simgesine tıklayarak şehir seçimi yapabilirsiniz.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const params: { search?: string, mode?: string } = {};
        if (searchTerm) {
          params.search = searchTerm;
        }
        if (selectedMode) {
          params.mode = selectedMode;
        }
        
        console.log(`Hatlar API isteği yapılıyor - Arama: ${searchTerm || 'Yok'}, Mod: ${selectedMode}`);
        
        if (controller) {
          controller.abort();
          controller = null;
        }
        
        controller = new AbortController();
        timeoutId = setTimeout(() => controller?.abort(), 15000);
        
        try {
          const response = await api.getBusLines(params);
          
          if (!isMounted) return;
          
          if (response && response.status === "success" && response.data) {
            setAllLines(response.data);
            applyFilters(response.data, selectedMode, searchTerm);
            console.log('Hatlar başarıyla yüklendi, toplam:', response.data.length);
          } else {
            setAllLines([]);
            setFilteredLines([]);
            console.warn('API yanıtında data bulunamadı');
            setError('Sunucudan geçerli bir yanıt alınamadı. Lütfen daha sonra tekrar deneyin.');
          }
        } catch (fetchErr: any) {
          if (!isMounted) return;
          
          if (fetchErr.name === 'AbortError') {
            setError('Sunucu yanıt vermedi. İstek zaman aşımına uğradı.');
          } else {
            throw fetchErr;
          }
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        }
      } catch (err: any) {
        if (!isMounted) return;
        
        console.error('Hatlar yüklenirken hata oluştu:', err);
        
        let errorMessage = 'Hat bilgileri yüklenirken bir sorun oluştu.';
        
        if (err.message && err.message.includes('Failed to fetch')) {
          errorMessage = 'Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.';
        } else if (err.message && err.message.includes('404')) {
          errorMessage = 'Hat bilgileri bulunamadı. Seçili şehir için bu hizmet henüz mevcut olmayabilir.';
        } else if (err.message && err.message.includes('500')) {
          errorMessage = 'Sunucuda bir hata oluştu. Teknik ekibimiz sorunu çözmek için çalışıyor. Lütfen daha sonra tekrar deneyin.';
        } else if (err.message && err.message.includes('timeout')) {
          errorMessage = 'Sunucu yanıt vermiyor. Lütfen daha sonra tekrar deneyin.';
        } else if (err.message && err.message.includes('API adresi tanımlanmamış')) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setAllLines([]);
        setFilteredLines([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const timeoutRef = setTimeout(fetchLines, 0);
    
    return () => {
      isMounted = false;
      clearTimeout(timeoutRef);
      
      if (controller) {
        controller.abort();
      }
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (allLines.length > 0) {
      applyFilters(allLines, selectedMode, searchTerm);
    }
  }, [selectedMode, applyFilters, allLines, searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    
    if (newSearchTerm === '' && allLines.length > 0) {
      applyFilters(allLines, selectedMode, '');
    }
  };

  const handleModeChange = (mode: TransportMode) => {
    setSelectedMode(mode);
  };

  const handleLineClick = (line: BusLine, mode: TransportMode) => {
    // Ulaşım moduna göre doğru URL'ye yönlendir
    navigate(`/lines/${mode}/${line.bus_line_id}`);
  };

  return (
    <div className={styles.linesPage}>
      {/* Premium üyeleri dışındaki kullanıcılara reklam gösterme - başlığın üstünde */}
      {!isPremium && <AdBanner className={styles.linesAdBanner} />}
      
      <div className={styles.glassCard}>
        <h1>Hatlar</h1>
        
        {activeTransportModes.length > 0 ? (
          <div className={styles.transportModes}>
            {activeTransportModes.map(mode => (
              <button
                key={mode.id}
                className={`${styles.modeButton} ${selectedMode === mode.id ? styles.activeModeButton : ''}`}
                onClick={() => handleModeChange(mode.id)}
              >
                {mode.name}
              </button>
            ))}
          </div>
        ) : null}
        
        <div className={styles.searchContainer}>
          <input 
            type="text" 
            placeholder="Hat adı veya numarası ile ara..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={styles.searchInput}
            disabled={hasNoActiveTransportModes}
          />
        </div>
      </div>
      
      {loading && <div className={styles.loadingMessage}>
        {cityLoading ? 'Şehir bilgileri yükleniyor...' : 'Hatlar yükleniyor...'}
      </div>}
      
      {hasNoActiveTransportModes && !loading && (
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>
            <div className={styles.errorIcon}>⚠️</div>
            <div className={styles.errorText}>
              <h3>Desteklenmeyen Özellik</h3>
              <p>Seçili şehir için aktif bir ulaşım türü bulunamadı. Lütfen başka bir şehir seçin veya daha sonra tekrar deneyin.</p>
              <button 
                className={styles.retryButton}
                onClick={() => setError(null)}
              >
                Şehir Değiştir
              </button>
            </div>
          </div>
        </div>
      )}
      
      {error && !hasNoActiveTransportModes && (
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>
            <div className={styles.errorIcon}>⚠️</div>
            <div className={styles.errorText}>
              <h3>Hata</h3>
              <p>{error}</p>
              <button 
                className={styles.retryButton}
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  setTimeout(() => {
                    const fetchData = async () => {
                      try {
                        const response: ApiResponse = await api.getBusLines({});
                        if (response && response.data) {
                          setAllLines(response.data);
                          applyFilters(response.data, selectedMode, searchTerm);
                          setError(null);
                        }
                      } catch (e) {
                        setError('Yeniden yükleme başarısız oldu. Lütfen sayfayı yenileyin.');
                      } finally {
                        setLoading(false);
                      }
                    };
                    fetchData();
                  }, 1000);
                }}
              >
                Yeniden Dene
              </button>
            </div>
          </div>
        </div>
      )}
      
      {!loading && !error && filteredLines.length === 0 && !hasNoActiveTransportModes && (
        <div className={styles.noResultsMessage}>
          {searchTerm 
            ? `"${searchTerm}" aramanıza uygun hat bulunamadı.` 
            : `${activeTransportModes.find(m => m.id === selectedMode)?.name || selectedMode} türünde hat bulunamadı.`}
        </div>
      )}
      
      <div className={styles.linesListContainer}>
        <ul className={styles.linesList}>
          {filteredLines.map((line) => (
            <li 
              key={line.bus_line_id} 
              className={styles.lineItem}
              onClick={() => handleLineClick(line, selectedMode)}
            >
              <LineChip 
                lineNumber={line.bus_line_id} 
                size="large" 
                lineColor={line.bus_line_color || undefined}
              />
              <div className={styles.lineInfo}>
                <div className={styles.lineName}>{line.bus_line_name}</div>
                <div className={styles.lineTerminals}>
                  <span className={styles.terminalStop}>{line.start_bus_stop}</span>
                  <span className={styles.separator}>→</span>
                  <span className={styles.terminalStop}>{line.end_bus_stop}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Sayfanın alt kısmına da bir reklam ekleyelim */}
      {!isPremium && filteredLines.length > 0 && <AdBanner className={styles.bottomAdBanner} />}
    </div>
  );
}

export default LinesPage;
